'use client';

import { useState, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Check, Lightbulb, Globe2, Mic, Sparkles, Shield } from 'lucide-react';
import StepIdea from './StepIdea';
import StepVoice from './StepVoice';
import StepLanguage from './StepLanguage';
import { MagicalGenerationModal } from '../magical-generation-modal';
import { InsufficientCreditsModal } from '../insufficient-credits-modal';
import type { AudioBookLanguage } from '@/lib/site-config';

type Step = 'idea' | 'language' | 'voice';

interface WizardState {
  idea: string;
  voiceId: string;
  primaryLanguage: AudioBookLanguage;
  bilingualMode: boolean;
  secondaryLanguage: AudioBookLanguage;
  montessoriMode: boolean;
  pageCount?: number; // Admin-only: custom page count for YouTube videos
}

// 3 active steps + visual ✨ indicator
const STEPS: { id: Step; label: string; icon: string }[] = [
  { id: 'idea', label: 'Idea', icon: '💡' },
  { id: 'language', label: 'Language', icon: '🌍' },
  { id: 'voice', label: 'Voice', icon: '🎤' },
];

const GENERATE_ICON = '✨'; // Visual indicator only

export default function CreateWizardMobile() {
  const { data: session } = useSession();
  const router = useRouter();
  
  const [currentStep, setCurrentStep] = useState<Step>('idea');
  const [wizardState, setWizardState] = useState<WizardState>({
    idea: '',
    voiceId: '',
    primaryLanguage: 'en',
    bilingualMode: false,
    secondaryLanguage: 'es',
    montessoriMode: false,
    pageCount: undefined, // undefined = use default (12 pages)
  });
  
  const [credits, setCredits] = useState(0);
  const [creditsLoaded, setCreditsLoaded] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [audioBookId, setAudioBookId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentStepLabel, setCurrentStepLabel] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (session?.user) {
      fetchUserData();
    } else {
      // For non-logged-in users, fetch voices and set credits to 0
      fetchVoicesForGuest();
    }
    
    // Restore wizard state after login/purchase
    const savedWizard = sessionStorage.getItem('pendingAudioBookWizard');
    if (savedWizard) {
      try {
        const parsed = JSON.parse(savedWizard);
        // Only restore if saved within last 30 minutes
        if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          // Merge saved state with defaults to handle missing fields
          setWizardState({
            idea: parsed.wizardState.idea || '',
            voiceId: parsed.wizardState.voiceId || '',
            primaryLanguage: parsed.wizardState.primaryLanguage || 'en',
            bilingualMode: parsed.wizardState.bilingualMode || false,
            secondaryLanguage: parsed.wizardState.secondaryLanguage || 'es',
            montessoriMode: parsed.wizardState.montessoriMode || false,
          });
          setCurrentStep(parsed.currentStep);
          
          // If user just logged in and has credits, show a message
          if (session?.user) {
            const checkCreditsAndProceed = async () => {
              const accountRes = await fetch('/api/account');
              if (accountRes.ok) {
                const data = await accountRes.json();
                if (data.credits >= 3) {
                  // User now has credits, they can proceed with generation
                  alert('Welcome back! You can now generate your audio picture book.');
                }
              }
            };
            checkCreditsAndProceed();
          }
        } else {
          // Clear expired saved state
          sessionStorage.removeItem('pendingAudioBookWizard');
        }
      } catch (e) {
        console.error('Failed to restore wizard state:', e);
        sessionStorage.removeItem('pendingAudioBookWizard');
      }
    }
  }, [session]);

  const fetchUserData = async () => {
    try {
      // Fetch account info (includes credits, subscription, etc.)
      const accountRes = await fetch('/api/account');
      if (accountRes.ok) {
        const data = await accountRes.json();
        setCredits(data.credits || 0);
        setIsSubscriber(data.isSubscriber || false);
        setIsPro(data.subscriptionPlan === 'AMAYA_PRO');
        
        // Fetch admin status from server-side API (environment variables aren't available client-side)
        const adminRes = await fetch('/api/admin/check');
        if (adminRes.ok) {
          const adminData = await adminRes.json();
          
          console.log('🔍 ADMIN CHECK DEBUG (MOBILE):');
          console.log('  - User Email:', adminData.email);
          console.log('  - Session Object:', session);
          console.log('  - isAdmin() Result:', adminData.isAdmin);
          console.log('  - Configured Admin Emails:', adminData.configuredEmails);
          console.log('  - Server Message:', adminData.message);
          
          setIsAdminUser(adminData.isAdmin);
          
          // Store configured emails for debug panel
          if (typeof window !== 'undefined') {
            (window as any).__adminEmails = adminData.configuredEmails;
          }
          
          if (adminData.isAdmin) {
            console.log('✅ Admin user CONFIRMED (mobile):', adminData.email);
            console.log('  - You should see the admin badge and 50-page control');
          } else {
            console.log('❌ NOT an admin user (mobile):', adminData.email);
            console.log('  - Configured admins:', adminData.configuredEmails);
            console.log('  - Check if your email is in ADMIN_EMAILS environment variable');
            console.log('  - Email must match exactly (case-sensitive)');
          }
        } else {
          console.error('Failed to check admin status:', adminRes.status);
          setIsAdminUser(false);
        }
        
        setCreditsLoaded(true);
      } else {
        // If API fails, set defaults and mark as loaded
        console.error('Failed to fetch account:', accountRes.status);
        setCredits(0);
        setIsSubscriber(false);
        setIsPro(false);
        setCreditsLoaded(true);
      }

      // Fetch default voice
      const voicesRes = await fetch('/api/voices');
      if (voicesRes.ok) {
        const data = await voicesRes.json();
        const voices = data.voices || [];
        if (voices.length > 0 && !wizardState.voiceId) {
          setWizardState(prev => ({ ...prev, voiceId: voices[0].voiceId }));
          setVoiceName(voices[0].name);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Always set creditsLoaded to true, even on error
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);
    }
  };

  const fetchVoicesForGuest = async () => {
    try {
      // Set credits to 0 for guest users and mark as loaded
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);

      // Fetch default voice so guests can preview
      const voicesRes = await fetch('/api/voices');
      if (voicesRes.ok) {
        const data = await voicesRes.json();
        const voices = data.voices || [];
        if (voices.length > 0 && !wizardState.voiceId) {
          setWizardState(prev => ({ ...prev, voiceId: voices[0].voiceId }));
          setVoiceName(voices[0].name);
        }
      }
    } catch (error) {
      console.error('Error fetching voices for guest:', error);
      // Always set creditsLoaded to true, even on error
      setCredits(0);
      setIsSubscriber(false);
      setIsPro(false);
      setCreditsLoaded(true);
    }
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'idea':
        return wizardState.idea.trim().length > 0;
      case 'language':
        return wizardState.primaryLanguage.length > 0;
      case 'voice':
        return wizardState.voiceId.length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceed()) return;
    
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    
    // If on the last step (Voice), auto-generate
    if (currentIndex === STEPS.length - 1) {
      handleSubmit();
      return;
    }
    
    // Otherwise move to next step
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  };

  const handleInspireMe = (composedPrompt: string) => {
    setWizardState(prev => ({ ...prev, idea: composedPrompt }));
    setCurrentStep('idea');
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleSubmit = async () => {
    // Step 1: Check if user is logged in
    if (!session?.user) {
      // Save wizard state to sessionStorage so we can restore it after login
      sessionStorage.setItem('pendingAudioBookWizard', JSON.stringify({
        wizardState,
        currentStep: 'voice',
        timestamp: Date.now()
      }));
      
      // Redirect to login page with callback to this page
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    // Step 2: Check if user has credits (everyone needs credits, including subscribers)
    if (credits < 3) {
      // Save wizard state before showing modal
      sessionStorage.setItem('pendingAudioBookWizard', JSON.stringify({
        wizardState,
        currentStep: 'voice',
        timestamp: Date.now()
      }));
      
      // Show the beautiful insufficient credits modal
      setShowInsufficientCreditsModal(true);
      return;
    }

    // Step 3: Proceed with generation
    setGenerating(true);
    setProgress(0);
    setCurrentStepLabel('🎨 Bringing your story to life...');

    try {
      const response = await fetch('/api/audio-book/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: wizardState.idea,
          voiceId: wizardState.voiceId,
          language: wizardState.primaryLanguage,
          learnMode: wizardState.bilingualMode,
          sourceLanguage: wizardState.primaryLanguage,
          targetLanguage: wizardState.secondaryLanguage,
          montessoriMode: wizardState.montessoriMode,
          pageCount: wizardState.pageCount, // Admin-only: custom page count
          // Themes will be auto-generated from the prompt
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAudioBookId(data.audioBookId);
        pollProgress(data.audioBookId);
        
        // Clear saved wizard state after successful generation
        sessionStorage.removeItem('pendingAudioBookWizard');
      } else {
        if (data.error === 'Insufficient credits') {
          setGenerating(false);
          setShowInsufficientCreditsModal(true);
        } else {
          throw new Error(data.error || 'Failed to generate audio book');
        }
      }
    } catch (error: any) {
      console.error('Generation error:', error);
      alert(error.message || 'Failed to generate audio book. Please try again.');
      setGenerating(false);
    }
  };

  const handlePurchase = async (packageType: string) => {
    setShowInsufficientCreditsModal(false);
    
    try {
      const res = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageType }),
      });

      if (!res.ok) throw new Error('Checkout failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  const handleSubscribe = async (plan?: 'amaya_plus' | 'amaya_pro') => {
    setShowInsufficientCreditsModal(false);
    
    const subscriptionPlan = plan || 'amaya_plus';
    
    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: subscriptionPlan }),
      });

      if (!res.ok) throw new Error('Subscription failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
    }
  };

  const pollProgress = (bookId: string) => {
    console.log('[CreateWizardMobile] Starting progress polling for:', bookId);
    
    // Track progress simulation - smooth animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        // Slow down as we approach completion
        const increment = prev < 50 ? 2 : prev < 80 ? 1 : 0.5;
        const newProgress = Math.min(prev + increment, 97); // Cap at 97%, reach 100% only on completion
        return newProgress;
      });
    }, 1500); // Update every 1.5 seconds for smooth progress
    
    // Poll actual status from correct endpoint
    let pollCount = 0;
    const statusInterval = setInterval(async () => {
      pollCount++;
      try {
        // Use the correct status endpoint that doesn't require pagesData
        const res = await fetch(`/api/audio-book/status?id=${bookId}`);
        
        // Handle different response codes properly
        if (res.status === 404) {
          console.error('[CreateWizardMobile] Audio book not found:', bookId);
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          alert('Audio book not found. Please try again.');
          setGenerating(false);
          return;
        }
        
        if (res.status === 422) {
          // This is normal during generation - book exists but no pagesData yet
          console.log(`[CreateWizardMobile] Poll #${pollCount}: Still generating (422 - no content yet)`);
          return; // Continue polling
        }
        
        if (!res.ok) {
          console.warn(`[CreateWizardMobile] Poll #${pollCount}: Status ${res.status}`);
          return; // Continue polling
        }

        const data = await res.json();
        console.log(`[CreateWizardMobile] Poll #${pollCount}: Status = ${data.status}`);

        if (data.status === 'completed') {
          console.log('[CreateWizardMobile] ✅ Generation completed! Redirecting...');
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          setProgress(100);
          setCurrentStepLabel('🎉 Your audio picture book is ready!');
          setTimeout(() => {
            router.push(`/audio-book/${bookId}`);
          }, 1000);
        } else if (data.status === 'failed') {
          console.error('[CreateWizardMobile] ❌ Generation failed');
          clearInterval(progressInterval);
          clearInterval(statusInterval);
          alert('Generation failed. Please try again.');
          setGenerating(false);
        } else if (data.status === 'pending') {
          console.log(`[CreateWizardMobile] Still pending (poll #${pollCount})...`);
          // Progress is already being updated by progressInterval
        }
      } catch (error) {
        console.error('[CreateWizardMobile] Polling error:', error);
        // Don't stop polling on network errors, might be temporary
      }
    }, 3000); // Poll every 3 seconds

    // Cleanup after 10 minutes (extended timeout for longer generations)
    setTimeout(() => {
      console.warn('[CreateWizardMobile] Polling timeout after 10 minutes');
      clearInterval(progressInterval);
      clearInterval(statusInterval);
      alert('Generation is taking longer than expected. Please check your library in a few minutes.');
      setGenerating(false);
    }, 600000);
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <>
      {/* 🎨 2026 Award-Winning Mobile Design - Glassmorphism & Premium Effects */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes gentle-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        @keyframes float-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes sparkle-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .mobile-wizard-container {
          animation: float-in 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .step-card-mobile {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .step-card-mobile:active {
          transform: scale(0.98);
        }

        /* Glassmorphism effect */
        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.08),
            0 2px 8px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        /* Premium gradient backgrounds */
        .premium-gradient {
          background: linear-gradient(135deg, 
            rgba(197, 180, 255, 0.95) 0%, 
            rgba(181, 242, 224, 0.95) 100%
          );
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
        }
      `}</style>

      <div className="mobile-wizard-container" style={{
        minHeight: '70vh',
        background: 'linear-gradient(180deg, #FDFBFF 0%, #FFF9F5 30%, #FFF5F0 60%, #FFEFE8 100%)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '0px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Admin Badge - Shows when admin user is logged in */}
        {isAdminUser && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem 1rem 0.5rem',
            position: 'relative',
            zIndex: 10,
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 0.875rem',
              background: 'linear-gradient(135deg, #F59E0B, #EF4444)',
              color: 'white',
              borderRadius: '16px',
              fontSize: '0.75rem',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            }}>
              <Shield size={14} />
              <span>ADMIN: Up to 50 pages</span>
            </div>
          </div>
        )}

        {/* Debug Panel - Shows admin check status (mobile) */}
        {session?.user && creditsLoaded && (() => {
          const adminEmails = typeof window !== 'undefined' ? (window as any).__adminEmails || [] : [];
          return (
            <div style={{
              margin: '0.75rem 1rem',
              padding: '0.875rem',
              background: isAdminUser 
                ? 'linear-gradient(135deg, #D1FAE5, #A7F3D0)' 
                : 'linear-gradient(135deg, #FEE2E2, #FECACA)',
              border: `2px solid ${isAdminUser ? '#10B981' : '#EF4444'}`,
              borderRadius: '12px',
              fontSize: '0.75rem',
              position: 'relative',
              zIndex: 10,
            }}>
              <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#1F2937' }}>
                🔍 Admin Status
              </div>
              <div style={{ color: '#374151', lineHeight: 1.5 }}>
                <div><strong>Email:</strong> {session.user.email || 'Not found'}</div>
                <div><strong>Status:</strong> {isAdminUser ? '✅ Admin' : '❌ Regular'}</div>
                <div><strong>Admin Emails:</strong> {adminEmails.length > 0 ? adminEmails.join(', ') : '⚠️ None'}</div>
                {!isAdminUser && adminEmails.length === 0 && (
                  <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#FEF3C7', borderRadius: '6px', color: '#92400E', fontSize: '0.7rem' }}>
                    ⚠️ ADMIN_EMAILS not set in Railway!
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        
        {/* Decorative floating elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          fontSize: '2rem',
          opacity: 0.15,
          animation: 'float-in 2s ease-in-out infinite alternate',
        }}>✨</div>
        <div style={{
          position: 'absolute',
          top: '30%',
          left: '8%',
          fontSize: '1.5rem',
          opacity: 0.12,
          animation: 'float-in 2.5s ease-in-out infinite alternate-reverse',
        }}>🎨</div>

        {/* Mobile Progress Dots - Enhanced 2026 Design */}
        <div className="glass-card" style={{
          padding: '1.25rem 1rem',
          margin: '1rem 1rem 0.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.75rem',
          borderRadius: '24px',
        }}>
          {STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            const canNavigate = isCompleted || isActive;
            
            return (
              <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Step clicked (mobile):', step.id, 'canNavigate:', canNavigate, 'isActive:', isActive, 'isCompleted:', isCompleted);
                    if (canNavigate) {
                      setCurrentStep(step.id);
                    }
                  }}
                  disabled={!canNavigate}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.375rem',
                    background: 'none',
                    border: 'none',
                    cursor: canNavigate ? 'pointer' : 'not-allowed',
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      width: isActive ? '58px' : '48px',
                      height: isActive ? '58px' : '48px',
                      borderRadius: '50%',
                      background: isActive 
                        ? 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 50%, #A8D8E6 100%)'
                        : isCompleted 
                        ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' 
                        : 'rgba(240, 240, 250, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isActive ? '1.625rem' : '1.375rem',
                      transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: isActive 
                        ? '0 12px 40px rgba(197, 180, 255, 0.6), inset 0 2px 12px rgba(255, 255, 255, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.4), 0 0 20px rgba(197, 180, 255, 0.3)'
                        : isCompleted
                        ? '0 6px 24px rgba(16, 185, 129, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                        : '0 2px 12px rgba(0, 0, 0, 0.06)',
                      opacity: canNavigate ? 1 : 0.4,
                      pointerEvents: 'auto',
                      backdropFilter: 'blur(12px)',
                      border: isActive 
                        ? '3px solid rgba(255, 255, 255, 0.6)' 
                        : isCompleted 
                        ? '2px solid rgba(255, 255, 255, 0.5)'
                        : '2px solid rgba(224, 213, 240, 0.4)',
                      position: 'relative',
                      overflow: 'hidden',
                      transform: isActive ? 'scale(1)' : 'scale(1)',
                    }}
                    onTouchStart={(e) => {
                      if (canNavigate && !isActive) {
                        e.currentTarget.style.transform = 'scale(1.15)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (canNavigate) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                    aria-label={`${isCompleted ? 'Go back to' : ''} ${step.label} step`}
                  >
                    {/* Shimmer effect for active state */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent)',
                        pointerEvents: 'none',
                        animation: 'shimmer 2s ease-in-out infinite',
                      }} />
                    )}
                    
                    {/* Glow pulse for active state */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(197, 180, 255, 0.4), rgba(181, 242, 224, 0.4))',
                        filter: 'blur(12px)',
                        opacity: 0.7,
                        animation: 'gentle-pulse 2s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}
                    
                    {isCompleted ? (
                      <Check 
                        size={isActive ? 24 : 20} 
                        strokeWidth={3} 
                        style={{
                          color: 'white',
                          position: 'relative',
                          zIndex: 1,
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                        }}
                      />
                    ) : step.id === 'idea' ? (
                      <Lightbulb 
                        size={isActive ? 24 : 20} 
                        strokeWidth={2.5} 
                        style={{
                          color: isActive ? 'white' : '#9ca3af',
                          position: 'relative',
                          zIndex: 1,
                          filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                        }}
                      />
                    ) : step.id === 'language' ? (
                      <Globe2 
                        size={isActive ? 24 : 20} 
                        strokeWidth={2.5} 
                        style={{
                          color: isActive ? 'white' : '#9ca3af',
                          position: 'relative',
                          zIndex: 1,
                          filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                        }}
                      />
                    ) : (
                      <Mic 
                        size={isActive ? 24 : 20} 
                        strokeWidth={2.5} 
                        style={{
                          color: isActive ? 'white' : '#9ca3af',
                          position: 'relative',
                          zIndex: 1,
                          filter: isActive ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))' : 'none',
                        }}
                      />
                    )}
                  </div>
                  {isActive && (
                    <div style={{
                      fontSize: '0.8125rem',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #7C3AED 0%, #C5B4FF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textAlign: 'center',
                      letterSpacing: '0.02em',
                      textShadow: '0 2px 4px rgba(124, 58, 237, 0.1)',
                    }}>
                      {step.label}
                    </div>
                  )}
                </button>
                {index < STEPS.length - 1 && (
                  <div style={{
                    width: '24px',
                    height: '4px',
                    background: isCompleted 
                      ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' 
                      : 'linear-gradient(90deg, rgba(224, 213, 240, 0.8), rgba(224, 213, 240, 0.5))',
                    borderRadius: '3px',
                    pointerEvents: 'none',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isCompleted ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
                  }} />
                )}
              </div>
            );
          })}
          
          {/* Visual ✨ indicator (not a clickable step) - Enhanced 2026 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.375rem',
          }}>
            <div
              style={{
                width: generating ? '54px' : '44px',
                height: generating ? '54px' : '44px',
                borderRadius: '50%',
                background: generating 
                  ? 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)'
                  : 'rgba(224, 213, 240, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: generating ? '1.5rem' : '1.25rem',
                transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: generating 
                  ? '0 8px 32px rgba(16, 185, 129, 0.6), inset 0 2px 12px rgba(255, 255, 255, 0.4), 0 0 20px rgba(16, 185, 129, 0.4)' 
                  : 'none',
                opacity: generating ? 1 : 0.35,
                backdropFilter: 'blur(8px)',
                border: generating ? '2px solid rgba(255, 255, 255, 0.5)' : '2px solid rgba(224, 213, 240, 0.3)',
                animation: generating ? 'sparkle-rotate 3s linear infinite' : 'none',
                position: 'relative',
                overflow: 'visible',
              }}
              aria-live="polite"
              aria-label={generating ? "Generating your audio book..." : "Generate step indicator"}
            >
              {/* Rotating glow ring for generating state */}
              {generating && (
                <div style={{
                  position: 'absolute',
                  inset: '-6px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.5), rgba(110, 231, 183, 0.3))',
                  filter: 'blur(12px)',
                  animation: 'gentle-pulse 2s ease-in-out infinite',
                  pointerEvents: 'none',
                }} />
              )}
              <span style={{ position: 'relative', zIndex: 1 }}>{GENERATE_ICON}</span>
            </div>
            {generating && (
              <div style={{
                fontSize: '0.8125rem',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textAlign: 'center',
                letterSpacing: '0.02em',
              }}>
                Generate
              </div>
            )}
          </div>
        </div>

        {/* Step Content - Scrollable */}
        <div 
          ref={containerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0 1rem 1rem',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {currentStep === 'idea' && (
            <StepIdea
              value={wizardState.idea}
              onChange={(value) => setWizardState(prev => ({ ...prev, idea: value }))}
              onQuickGenerate={() => setCurrentStep('language')}
              montessoriMode={wizardState.montessoriMode}
              onMontessoriModeChange={(enabled) => setWizardState(prev => ({ ...prev, montessoriMode: enabled }))}
              isSubscriber={isSubscriber}
            />
          )}

          {currentStep === 'language' && (
            <StepLanguage
              primaryLanguage={wizardState.primaryLanguage}
              onPrimaryLanguageChange={(lang) => setWizardState(prev => ({ ...prev, primaryLanguage: lang }))}
              bilingualMode={wizardState.bilingualMode}
              onBilingualModeChange={(enabled) => setWizardState(prev => ({ ...prev, bilingualMode: enabled }))}
              secondaryLanguage={wizardState.secondaryLanguage}
              onSecondaryLanguageChange={(lang) => setWizardState(prev => ({ ...prev, secondaryLanguage: lang }))}
              montessoriMode={wizardState.montessoriMode}
              onMontessoriModeChange={(enabled) => setWizardState(prev => ({ ...prev, montessoriMode: enabled }))}
              isSubscriber={isSubscriber}
              onNext={() => setCurrentStep('voice')}
            />
          )}

          {currentStep === 'voice' && (
            <>
              <StepVoice
                value={wizardState.voiceId}
                onChange={(voiceId) => {
                  setWizardState(prev => ({ ...prev, voiceId }));
                  fetch('/api/voices').then(async (r) => {
                    if (r.ok) {
                      const data = await r.json();
                      const voice = data.voices.find((v: any) => v.voiceId === voiceId);
                      if (voice) setVoiceName(voice.name);
                    }
                  });
                }}
                isSubscriber={isSubscriber}
                isPro={isPro}
                language={wizardState.primaryLanguage}
              />

              {/* Admin Page Count Control - Shows for admin users only */}
              {isAdminUser && wizardState.voiceId && !generating && (
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #FEF3C7, #FED7AA)',
                  border: '2px solid #F59E0B',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                  }}>
                    <Shield size={20} color="#F59E0B" />
                    <h3 style={{ margin: 0, color: '#92400E', fontSize: '1rem', fontWeight: 700 }}>
                      Admin: Custom Pages
                    </h3>
                  </div>
                  
                  <p style={{ color: '#92400E', marginBottom: '0.75rem', fontSize: '0.9rem', lineHeight: 1.4 }}>
                    Generate up to 50 pages for YouTube videos!
                  </p>
                  
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{
                      display: 'block',
                      color: '#92400E',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                      fontSize: '0.95rem',
                    }}>
                      Pages: {wizardState.pageCount || 12}
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="50"
                      value={wizardState.pageCount || 12}
                      onChange={(e) => setWizardState(prev => ({
                        ...prev,
                        pageCount: parseInt(e.target.value)
                      }))}
                      style={{
                        width: '100%',
                        height: '8px',
                        borderRadius: '4px',
                        appearance: 'none',
                        background: 'linear-gradient(90deg, #F59E0B, #EF4444)',
                        cursor: 'pointer',
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: '#92400E',
                      marginTop: '0.25rem',
                    }}>
                      <span>12</span>
                      <span>50</span>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '0.6rem',
                    background: 'rgba(255,255,255,0.7)',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    color: '#92400E',
                  }}>
                    <strong>Tip:</strong> 50 pages = 10-15 min video for YouTube! Same 3 credit cost.
                  </div>
                </div>
              )}

              {/* Generate Button - Shows when voice is selected - 2026 Premium Design */}
              {wizardState.voiceId && !generating && (
                <div className="glass-card" style={{
                  marginTop: '2rem',
                  padding: '1.75rem 1.25rem',
                  borderRadius: '28px',
                  textAlign: 'center',
                  animation: 'float-in 0.5s ease-out',
                }}>
                  {/* Premium Badge */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 193, 7, 0.15) 100%)',
                    borderRadius: '20px',
                    marginBottom: '1.25rem',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                  }}>
                    <span style={{ fontSize: '1rem' }}>⭐</span>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>Ready to Create Magic!</span>
                    <span style={{ fontSize: '1rem' }}>✨</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={generating || !creditsLoaded}
                    className="premium-gradient"
                    style={{
                      width: '100%',
                      padding: '1.375rem 2rem',
                      background: (generating || !creditsLoaded)
                        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '24px',
                      fontSize: '1.1875rem',
                      fontWeight: 900,
                      cursor: (generating || !creditsLoaded) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.875rem',
                      boxShadow: (generating || !creditsLoaded)
                        ? 'none'
                        : '0 12px 40px rgba(16, 185, 129, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      opacity: (generating || !creditsLoaded) ? 0.5 : 1,
                      position: 'relative',
                      overflow: 'hidden',
                      letterSpacing: '0.01em',
                    }}
                    onTouchStart={(e) => {
                      if (!generating && creditsLoaded) {
                        e.currentTarget.style.transform = 'scale(0.96) translateY(2px)';
                        e.currentTarget.style.boxShadow = '0 6px 24px rgba(16, 185, 129, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.2)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (!generating && creditsLoaded) {
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(16, 185, 129, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)';
                      }
                    }}
                  >
                    {/* Shimmer effect overlay */}
                    {!generating && creditsLoaded && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shimmer 3s ease-in-out infinite',
                        pointerEvents: 'none',
                      }} />
                    )}
                    
                    {!creditsLoaded ? (
                      <>
                        <span style={{ fontSize: '1.625rem', position: 'relative', zIndex: 1 }}>⏳</span>
                        <span style={{ position: 'relative', zIndex: 1 }}>Loading...</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '1.75rem', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}>🎨</span>
                        <span style={{ position: 'relative', zIndex: 1, textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}>Create Magic Now</span>
                        <span style={{ fontSize: '1.75rem', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}>✨</span>
                      </>
                    )}
                  </button>
                  
                  {/* Info Pills - Enhanced Design */}
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    justifyContent: 'center',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1rem',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '16px',
                      border: '1px solid rgba(197, 180, 255, 0.2)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    }}>
                      <span style={{ fontSize: '1.125rem' }}>💳</span>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#7C3AED',
                      }}>3 credits</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1rem',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '16px',
                      border: '1px solid rgba(181, 242, 224, 0.3)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    }}>
                      <span style={{ fontSize: '1.125rem' }}>⏱️</span>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#10b981',
                      }}>~2-3 mins</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1rem',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '16px',
                      border: '1px solid rgba(168, 216, 230, 0.3)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    }}>
                      <span style={{ fontSize: '1.125rem' }}>🎧</span>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#0ea5e9',
                      }}>Pro Quality</span>
                    </div>
                  </div>

                  {/* Trust Indicator */}
                  <div style={{
                    marginTop: '1.25rem',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, rgba(197, 180, 255, 0.1) 0%, rgba(181, 242, 224, 0.1) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(197, 180, 255, 0.15)',
                  }}>
                    <div style={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: '#64748b',
                      lineHeight: 1.5,
                    }}>
                      <span style={{ fontSize: '1rem', marginRight: '0.375rem' }}>🌟</span>
                      Join thousands of parents creating magical learning experiences
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      <MagicalGenerationModal
        isGenerating={generating}
        progress={progress}
        currentStep={currentStepLabel}
        videoUrl="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/loading-audio-book.mp4"
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onPurchase={handlePurchase}
        onSubscribe={handleSubscribe}
      />
    </>
  );
}

